/**
 * Local Player Class
 *
 * Extends RemotePlayer with client-side prediction for the local player
 * Handles input buffering, prediction, and server reconciliation
 */

import { Movement } from '../shared/Movement.js';
import { rebuildInventorySlot, applySlotAmmo } from '../shared/Player.js';
import { RemotePlayer } from './RemotePlayer.js';
import { gameState } from './client.js';
import { GameState } from './core/GameState.js';
import { ClientLogger } from './logger/ClientLogger.js';

const localPlayerLogger = (() => {
  if (ClientLogger) {
    return ClientLogger.getLogger('player');
  }
  if (typeof console !== 'undefined') {
    return console;
  }
  return { warn: () => {}, info: () => {}, error: () => {}, log: () => {} };
})();

class LocalPlayer extends RemotePlayer {
  constructor(config) {
    super(config);
    this.isLocal = true;
    this.isDead = false; // Track if local player is dead
    this.inputHistory = []; // {seq, input, sendTime}
    this.inputSeq = 0;
    this.lastShotTime = 0;
    this.lastAckSeq = 0;
    // Drift state the server has acknowledged (replay base); this.drift is the predicted one.
    this._ackedDrift = null;

    // Visual correction offset (visual only, not gameplay). The sprite is drawn at the
    // exact predicted position PLUS this offset; a reconciliation correction moves the
    // predicted position instantly and puts the opposite amount here, which then decays
    // to zero over a few frames. Steady-state rendering therefore has zero added latency
    // (the old whole-position smoother lagged every movement by ~75 ms).
    this.visualOffsetX = 0;
    this.visualOffsetY = 0;

    // Cache last render state so multiple calls in the same frame stay in sync
    this._renderCacheTime = null;
    this._cachedRenderState = { x: this.x, y: this.y, angle: this.angle };
  }

  /**
   * Override RemotePlayer.updateFromServer to preserve locally predicted motion.
   *
   * A snapshot's position/velocity/angle are one RTT stale. The local player's
   * position is owned by predict() + reconcile() (which replays the inputs the
   * server has not acked yet), and its angle by local input, so we restore both
   * after the base class merges everything the server IS authoritative for
   * (health, ammo, inventory, cosmetics, ability effects).
   *
   * Without this, updateFromState() would overwrite this.x/y with the acked
   * position before reconcile() ever runs — making reconcile's "error" the
   * length of the unacked input replay rather than genuine prediction drift,
   * so RECONCILE_THRESHOLD could never distinguish the two.
   */
  updateFromServer(state, isKeyframe = false, serverTime = null) {
    const predicted = { x: this.x, y: this.y, vx: this.vx, vy: this.vy, angle: this.angle, drift: this.drift };
    super.updateFromServer(state, isKeyframe, serverTime);
    if ('drift' in state) this._ackedDrift = state.drift;   // authoritative base for reconcile()
    this.x = predicted.x;
    this.y = predicted.y;
    this.vx = predicted.vx;
    this.vy = predicted.vy;
    this.angle = predicted.angle;
    this.drift = predicted.drift;
    this._renderCacheTime = null;
  }

  /**
   * Predict one fixed tick of local movement with the SAME function the server runs.
   * @param {number} dt - fixed tick in seconds (GameLoop.fixedDt = 1/60)
   * @param {Object} input - { left, right, up, down }
   */
  predict(dt, input) {
    const next = Movement.stepPlayer(
      { x: this.x, y: this.y, drift: this.drift || Movement.initialDrift(GameState.DRIFT) },
      input,
      dt,
      this._movementParams(),
      gameState.colliders
    );
    this.x = next.x;
    this.y = next.y;
    this.vx = next.vx;
    this.vy = next.vy;
    this.drift = next.drift;
  }

  _movementParams() {
    const mult = this.activeAbilityEffects?.speedMultiplier;
    return {
      speedPxPerSec: GameState.MOVEMENT.speedPxPerSec,
      radius: GameState.MOVEMENT.radius,
      speedMultiplier: typeof mult === 'number' && Number.isFinite(mult) ? Math.max(0, mult) : 1,
      driftBlocked: Boolean(this.activeAbilityEffects?.blocksDrift),
      drift: GameState.DRIFT
    };
  }

  /**
   * Reconcile with server state
   * Uses smooth correction for small discrepancies to avoid visible snapping
   * @param {Object} serverState - Authoritative server state
   * @param {number} snapThreshold - Error above which the simulation is corrected
   * @param {number} _smoothFactorUnused - Kept for call-site compatibility; smoothing is visual now
   */
  reconcile(serverState, snapThreshold = 2, _smoothFactorUnused = 0) {
    const processedSeq = serverState.lastProcessedSeq || 0;

    // Track last acknowledged seq for input resend logic
    this.lastAckSeq = processedSeq;

    // Remove processed inputs
    while (this.inputHistory.length > 0 && this.inputHistory[0].seq <= processedSeq) {
      this.inputHistory.shift();
    }

    if ('drift' in serverState) this._ackedDrift = serverState.drift;

    if (typeof serverState.x !== 'number' || typeof serverState.y !== 'number') {
      // Delta without position: nothing to reconcile positionally, but the
      // non-positional fields (health, ammo, inventory, effects) still apply.
      this._applyServerFields(serverState);
      return;
    }

    // Replay unacknowledged inputs on top of the server-acked state with the
    // same step function and colliders the server used. In steady state the
    // result equals our predicted position exactly; any error is a real
    // correction (teleport, container opened under us, ability timing).
    let target = { x: serverState.x, y: serverState.y, drift: this._ackedDrift || this.drift || Movement.initialDrift(GameState.DRIFT) };
    const params = this._movementParams();
    for (const inputRecord of this.inputHistory) {
      target = Movement.stepPlayer(target, inputRecord.input, 1 / 60, params, gameState.colliders);
    }
    const targetX = target.x;
    const targetY = target.y;
    const targetVx = target.vx ?? this.vx;
    const targetVy = target.vy ?? this.vy;
    // The drift meter is not smoothed: the replayed value IS the prediction from here on.
    if (target.drift) this.drift = target.drift;

    // Calculate error between current predicted position and target
    const errorX = targetX - this.x;
    const errorY = targetY - this.y;
    const errorDist = Math.sqrt(errorX * errorX + errorY * errorY);

    if (errorDist > snapThreshold) {
      // A real correction (teleport, container opened under us, ability timing, packet
      // reorder): the SIMULATION snaps at once — it must agree with the server — and the
      // PICTURE catches up over a few frames via the visual offset, unless the jump is a
      // teleport-sized one, in which case hiding it would look worse than showing it.
      this.x = targetX;
      this.y = targetY;
      this.vx = targetVx;
      this.vy = targetVy;
      if (errorDist < LocalPlayer.RENDER_SNAP_DISTANCE) {
        this.visualOffsetX -= errorX;
        this.visualOffsetY -= errorY;
      } else {
        this.visualOffsetX = 0;
        this.visualOffsetY = 0;
      }
      gameState.netStats?.onCorrection?.(errorDist);
    }
    // Sub-threshold error: identical models, so this is float noise — leave it.

    this._applyServerFields(serverState);
  }

  /**
   * Apply the non-positional, server-authoritative fields of a snapshot.
   * Split out of reconcile() so position-less deltas still land these.
   * @param {Object} serverState
   * @private
   */
  _applyServerFields(serverState) {
    // Update weapon state from server (server is authoritative)
    if (serverState.weapon) {
      this.weaponState.currentAmmo = serverState.weapon.currentAmmo;
      this.weaponState.reloading = serverState.weapon.reloading;
      this.weaponState.reloadStartTime = serverState.weapon.reloadStartTime;
      this.weaponState.reloadTime = serverState.weapon.reloadTime;
    }

    // The ammo pouch (per type, shared by guns of that type). Deltas omit it
    // when it did not change.
    if (serverState.ammo && typeof serverState.ammo === 'object') {
      this.ammo = { ...serverState.ammo };
    }

    // Update health from server
    if (serverState.health) {
      this.health = serverState.health;
    }

    if (typeof serverState.shieldHealth === 'number') {
      this.shieldHealth = serverState.shieldHealth;
    }

    if (typeof serverState.maxShield === 'number') {
      this.maxShield = serverState.maxShield;
    }

    // Update cosmetics from server
    if (serverState.cosmetics) {
      this.cosmetics = serverState.cosmetics;
    }

    // Update inventory and activeSlotIndex from server
    if ('inventory' in serverState) {
      // Reconstruct inventory from serialized data
      if (Array.isArray(serverState.inventory)) {
        const previousInventory = this.inventory || [];
        this.inventory = serverState.inventory.map((slot, index) =>
          // An unresolvable ref is the one failure worth a line: it means the
          // client's registries disagree with the server's.
          rebuildInventorySlot(slot, previousInventory[index], (unresolved) => {
            localPlayerLogger.warn('[LocalPlayer] Slot item not in registry, preserving previous', {
              slotIndex: index,
              ref: unresolved.ref ?? unresolved.itemId
            });
          }));
      }
    }
    if ('activeSlotIndex' in serverState) {
      this.activeSlotIndex = serverState.activeSlotIndex;
    }

    if ('slotAmmo' in serverState) {
      applySlotAmmo(this.inventory, serverState.slotAmmo, this.activeSlotIndex);
    }

    if ('inventory' in serverState || 'activeSlotIndex' in serverState) {
      this._syncActiveWeaponState();
    }

    // Update active ability effects from server (for visual rendering)
    // Only update if explicitly included in state (delta compression may omit unchanged fields)
    if ('activeAbilityEffects' in serverState) {
      this.activeAbilityEffects = serverState.activeAbilityEffects || {};
    }

    // Update team from server (for visual rendering, e.g. team ring)
    // Only update if explicitly included in state (delta compression may omit unchanged fields)
    if ('team' in serverState) {
      this.team = serverState.team;
    }
  }

  /**
   * Get the render state for the local player.
   * Uses smoothed render position to avoid visual jitter from reconciliation.
   * @param {number} renderTime - Ignored for local player (used by remote players)
   * @returns {{ x: number, y: number, angle: number }}
   */
  getRenderState(renderTime) {
    if (this._renderCacheTime === renderTime && this._cachedRenderState) {
      return this._cachedRenderState;
    }

    // Decay the visual correction offset (once per rendered frame); the predicted
    // position itself is rendered with no smoothing.
    const decay = LocalPlayer.RENDER_OFFSET_DECAY;
    this.visualOffsetX *= decay;
    this.visualOffsetY *= decay;
    if (Math.abs(this.visualOffsetX) < 0.25) this.visualOffsetX = 0;
    if (Math.abs(this.visualOffsetY) < 0.25) this.visualOffsetY = 0;

    const state = { x: this.x + this.visualOffsetX, y: this.y + this.visualOffsetY, angle: this.angle };
    this._cachedRenderState = state;
    this._renderCacheTime = renderTime;
    return state;
  }
}

/** Corrections larger than this (teleport, rewind, respawn) snap the picture too. */
LocalPlayer.RENDER_SNAP_DISTANCE = 150;
/** Per-frame multiplier on the visual offset: 0.75 → a correction is ~95 % gone in 10 frames. */
LocalPlayer.RENDER_OFFSET_DECAY = 0.75;

export { localPlayerLogger, LocalPlayer };
